import { useForm } from 'react-hook-form';
import pMap from 'p-map';

import { zodResolver } from '@hookform/resolvers/zod';

import { useLazyQuery } from '@apollo/client/react';

import { Or2Dialog } from '@/components/feedback/Or2Dialog';
import { graphql, type DocumentType } from '@/gql';

import { Or2Button } from '@/components/button/Or2Button';

import { extractConnectionProps } from '@/utils/queries/queryExtracts';

import { nonFalsey } from '@/utils/nonFalsey';

import { useMutation } from '@/hooks/mutationWithSnackbar';

import type { PostTopicFormData } from '../../forms/PostTopicsForm';
import { PostTopicSchema, PostTopicsForm } from '../../forms/PostTopicsForm';

const POST_EDIT_TOPICS_DIALOG_QUERY = graphql(`
  query PostEditTopicsDialogQuery($groupId: ID!) {
    group(id: $groupId) {
      _id
      name
      referenceName
      userHasAdminAccess
      ownedPostTagsConnection {
        edges {
          node {
            _id
            displayName
          }
        }
      }
      type {
        _id
        hasCommitteesWithGroupType {
          _id
          containsGroupsConnection(filterOn: { parents: [$groupId] }) {
            edges {
              node {
                _id
                name
              }
            }
          }
        }
      }
    }
  }
`);

const SET_POST_TAGS_TO_POST_MUTATION = graphql(`
  mutation SetPostTagsToPost($data: SetPostTagsToPostInput) {
    setPostTagsToPost(params: $data) {
      post {
        ...postFields
      }
    }
  }
`);

const CREATE_POST_TAG_MUTATION = graphql(`
  mutation CreatePostTagMutation($params: CreatePostTagInput) {
    createPostTag(params: $params) {
      postTag {
        _id
        displayName
      }
    }
  }
`);

const POST_EDIT_TOPICS_DIALOG_POST_FRAGMENT = graphql(`
  fragment postEditTopicsDialogPostFragment on Post {
    _id
    hasPostLinkedObjectsConnection {
      edges {
        node {
          _id
          targetType
          target {
            ... on PostTag {
              _id
              displayName
            }
          }
        }
      }
    }
  }
`);

const POST_EDIT_TOPICS_DIALOG_GROUP_FRAGMENT = graphql(`
  fragment postEditTopicsDialogGroupFragment on Group {
    _id
  }
`);

interface Props {
  post: DocumentType<typeof POST_EDIT_TOPICS_DIALOG_POST_FRAGMENT>;
  group: DocumentType<typeof POST_EDIT_TOPICS_DIALOG_GROUP_FRAGMENT>;
  open: boolean;
  onClose: () => void;
}

export const PostEditTopicsDialog = ({ post, group, open, onClose }: Props) => {
  const { _id: postId, hasPostLinkedObjectsConnection } = post;
  const { _id: groupId } = group;

  const [getLabels, { data, loading: loadingQuery }] = useLazyQuery(
    POST_EDIT_TOPICS_DIALOG_QUERY,
  );

  const options = extractConnectionProps(data?.group?.ownedPostTagsConnection);

  const selectedTags = extractConnectionProps(hasPostLinkedObjectsConnection)
    .map((tag) => {
      if (tag.target?.__typename === 'PostTag') {
        return tag.target;
      }

      return null;
    })
    .filter(nonFalsey);

  const { control, handleSubmit, reset } = useForm<PostTopicFormData>({
    defaultValues: {
      tags: selectedTags,
    },
    resolver: zodResolver(PostTopicSchema),
  });

  const [setPostTagsToPost, { loading: loadingSet }] = useMutation(
    SET_POST_TAGS_TO_POST_MUTATION,
    {
      onCompleted: onClose,
    },
  );

  const [createPostTag, { loading: loadingCreate }] = useMutation(
    CREATE_POST_TAG_MUTATION,
    {
      onCompleted: () =>
        getLabels({
          variables: {
            groupId: groupId ?? '',
          },
        }),
    },
  );

  const loadingMutation = loadingSet || loadingCreate;

  const onSubmit = async (data: PostTopicFormData) => {
    const newTags = data.tags.filter((tag) => tag._id === null);

    const newTagIds = await pMap(
      newTags,
      async (tag) => {
        const newTag = await createPostTag({
          variables: {
            params: {
              ownerId: groupId,
              ownerType: 'Group',
              displayName: tag.displayName,
            },
          },
        });

        return newTag?.data?.createPostTag?.postTag?._id || false;
      },
      { concurrency: 3 },
    );

    const tagsToAdd = [...data.tags.map((tag) => tag._id), ...newTagIds].filter(
      nonFalsey,
    );

    setPostTagsToPost({
      variables: {
        data: {
          ownerId: groupId,
          ownerType: 'Group',
          postId,
          postTagIds: tagsToAdd,
        },
      },
    });
  };

  return (
    <Or2Dialog
      maxWidth="sm"
      title="Post topic"
      actions={
        <>
          <Or2Button color="secondary" onClick={onClose}>
            Close
          </Or2Button>
          <Or2Button
            disabled={loadingMutation}
            onClick={handleSubmit(onSubmit)}
          >
            Save
          </Or2Button>
        </>
      }
      slotProps={{
        transition: {
          onEnter: () => {
            reset();
            getLabels({
              variables: {
                groupId: groupId ?? '',
              },
            });
          },
        },
      }}
      loading={loadingMutation}
      open={open}
      onClose={onClose}
    >
      <PostTopicsForm
        control={control}
        options={options}
        loading={loadingQuery}
      />
    </Or2Dialog>
  );
};
